import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Field, FilePicker, InfoTip, inputCls } from '../components/shared/FormKit';
import {
  FormValidationProvider, useFormValidation, useFieldInvalid, FieldInvalidInline, INVALID_FIELD_ATTR,
} from '../components/shared/formValidation';
import {
  BIZFILE_DOC_TYPE, bizFileOf, bizFileKey, useBizFiles, BizFileUploads,
  useDuplicateEngagements, DuplicateEngagementWarning, DescriptionOfServiceNotice, descriptionNeededFrom,
  CompanyChecklist, COMPANIES_SECTION_HINT, companyRequiredGaps, feeRowsOf,
} from '../components/shared/EngagementCompanies';
import { lineFeeKey } from '../utils/serviceLines';
import { bizFileRequired } from '../components/shared/entityCategory';
import { fieldInvalidRing, fieldInvalidLabel } from '../components/workflow/constants';
import { FormShell, FormSection } from '../components/workflow/FormShell';
import ReviewerSelect, { useIsQmReviewer } from '../components/workflow/ReviewerSelect';
import {
  emptyEngagementFields, engagementRequiredGaps,
  EngagementDateFields, EngagementProfileFields, EngagementPeopleFields,
  EngagementScreeningQuestion, ENGREG_PEOPLE_HINT, profileSplitJson, companiesOf, slotQuestionOf,
  activeServiceLines, serviceLineClashes,
} from '../components/shared/EngagementFormFields';
import {
  emptyScreeningAnswers, screeningRequiredGaps, appendScreeningAnswers, ScreeningQuestionFields,
} from '../components/shared/ScreeningFields';
import { fetchJSON } from '../utils/api';
import { hintOf } from '../utils/uploadKinds';

// Engagement Registration — submission form (docs/modules/workflow-engreg.md).
// THE project start: pick one or several same-group
// companies (from Clients & Entities), one FYE, each company's service (plus
// any additional services under it), the multi-select leads and the
// client-profile questions. Approval creates one engagement per company and
// per additional service (status Pending Acceptance) — the QC6/QC7
// acceptance runs next.
//
// The fields themselves live in shared/EngagementFormFields.js:
// this flow is the REFERENCE shape, and Management → New
// engagement and Engagement Amendment render the very same blocks. What stays
// here is what only Registration does — covering several companies of one group
// with a single request, and the independence declaration.

// The default empty form state (the edit page prefills from the request).
export const emptyEngRegForm = () => ({
  ...emptyEngagementFields(),
  ...emptyScreeningAnswers(),
  groupId: '', entityIds: [],
  independenceDeclared: false,
  // The reviewer the submitter names (a Partner or a QM department member).
  reviewerStaffId: '',
});

// The BizFile is mandatory here: one PDF per covered Singapore company. The picker, the per-company file map and the live duplicate guard
// live in shared/EngagementCompanies.js — Management → New engagement renders
// the very same blocks. Re-exported for the edit page.
export { BIZFILE_DOC_TYPE, bizFileOf, useBizFiles, BizFileUploads, DuplicateEngagementWarning };

// The screening document. ONE per registration — not per company like the
// BizFile — so it is a single File, not a map. Only a No answer to "Does QM
// need to perform screening?" lets the requester attach one; Yes asks the six
// screening questions instead, and the approval opens a Screening request in
// which QM files its own document.
export const SCREENING_DOC_TYPE = 'SCREENING';
export const screeningFileOf = (files) =>
  (files || []).find((x) => x.docType === SCREENING_DOC_TYPE) || null;

// Singapore companies still without a BizFile — neither already attached nor
// picked now. A Government Agency / Overseas Company has no ACRA BizFile, so
// it is never a gap (shared/entityCategory.bizFileRequired).
const companiesMissingBizFile = (f, { bizFiles = {}, existing = [], entities = [] } = {}) =>
  (f.entityIds || [])
    .filter((id) => bizFileRequired((entities.find((e) => e.id === id) || {}).entityCategory))
    .filter((id) => !bizFiles[id] && !bizFileOf(existing, id))
    .map((id) => ({ id, name: (entities.find((e) => e.id === id) || {}).entityName || `company #${id}` }));

// Client-side required check → `[{ key, label }]` (server re-validates). `key`
// is the `field=` key of the control that is empty, so `formValidation` can
// mark it on the page; `label` is the banner wording.
export const engRegRequiredGaps = (f, bizCtx = {}) => {
  const gaps = companyRequiredGaps(f);
  // The shared blocks key their controls through this flow's anchor (people by
  // name list) — the gap keys go through the same mapping so they meet. Listed
  // in page order: sections ①–③ first, then Screening ④ and BizFile ⑤.
  // People are optional here: the requester is locked on the team. Only a
  // requester WITHOUT a staff record (`requesterStaffId === null`; undefined =
  // not known yet) must name somebody, or nobody could carry the project on.
  // screeningAsked = the named reviewer is in QM; otherwise the Screening
  // section is not on the form at all and the answer travels as No.
  const { requesterStaffId, multiFyeServices = [], screeningAsked = true } = bizCtx;
  engagementRequiredGaps(f, { multiFyeServices }).forEach((g) => gaps.push({ ...g, key: engRegAnchor(g.key) }));
  if (requesterStaffId === null) {
    const people = [f.partnerStaffIds, f.directorStaffIds, f.managerStaffIds, f.teamStaffIds];
    if (people.every((ids) => (ids || []).length === 0)) {
      gaps.push({ key: 'teamMembers', label: 'Partner in Charge, Director(s), Manager(s) or Team Member(s) (At Least One)' });
    }
  }
  // Asked here rather than in the shared profile check: the
  // question sits in this flow's Screening section, and Management → New
  // engagement — which shares the profile check — does not ask it at all.
  if (screeningAsked && f.screeningRequired === '') gaps.push({ key: 'screeningRequired', label: 'QM Screening' });
  // Yes ⇒ the six screening questions (they travel to the Screening request
  // the approval opens).
  if (screeningAsked && f.screeningRequired === 'yes') gaps.push(...screeningRequiredGaps(f, engRegAnchor));
  for (const { id, name } of companiesMissingBizFile(f, bizCtx)) {
    gaps.push({ key: bizFileKey(id), label: `BizFile (PDF) for ${name}` });
  }
  if (!f.reviewerStaffId) gaps.push({ key: 'reviewerName', label: 'Reviewer' });
  return gaps;
};

/** The same check as a list of labels — the edit page's banner. */
export const missingEngRegFields = (f, bizCtx) => engRegRequiredGaps(f, bizCtx).map((g) => g.label);

// FormData for POST/PATCH — one shape for both. screeningAsked = false (the
// reviewer is not in QM): the answer is No; the requester's own screening
// document still goes. Callers pass screeningFile only when it applies.
export const engRegFormData = (f, files, bizFiles = {}, screeningFile = null, screeningAsked = true) => {
  const form = new FormData();
  // bizfile_<entityId>: the server reads the company off the field name. Only
  // ever for a company still ticked — the server rejects the rest outright
  // ("uploaded for a company that is not on this registration"), and useBizFiles
  // already prunes them, so this is the second lock on the same door.
  const covered = new Set((f.entityIds || []).map((id) => String(id)));
  Object.entries(bizFiles).forEach(([entityId, file]) => {
    if (file && covered.has(String(entityId))) form.append(`bizfile_${entityId}`, file);
  });
  // Only ever sent when the answer is No — posting one with a Yes is a 400 by
  // design (QM attaches that copy itself at approval).
  if (screeningFile) form.append('screening', screeningFile);
  form.set('entityIds', JSON.stringify(f.entityIds));
  form.set('yearEnd', f.yearEnd);
  form.set('serviceProvided', f.service);
  if (f.service === 'Other') form.set('serviceOther', f.serviceOther.trim());
  form.set('descriptionOfService', f.descriptionOfService);
  form.set('ultimateParent', f.ultimateParent);
  form.set('overseasOperations', f.overseasOperations === 'yes' ? 'true' : 'false');
  form.set('consolidatedFs', f.consolidatedFs);
  form.set('isPie', f.isPie === 'yes' ? 'true' : 'false');
  form.set('pieType', f.isPie === 'yes' ? (f.pieType || '') : '');
  form.set('pieTypeOther', f.isPie === 'yes' ? (f.pieTypeOther || '') : '');
  form.set('proposedFees', String(f.proposedFees).trim());
  form.set('proposedFeesCcy', f.proposedFeesCcy || 'SGD');
  // Each covered company's own amount (two or more engagements only; blanks
  // drop) — a service line's amount travels on the line.
  const lines = activeServiceLines(f);
  const split = f.proposedFeesSplit || {};
  const feeSplit = Object.fromEntries(Object.entries(split)
    .filter(([id, v]) => covered.has(String(id)) && String(v).trim() !== ''));
  form.set('proposedFeesSplit', JSON.stringify(covered.size + lines.length > 1 ? feeSplit : {}));
  // The additional services — one more engagement each (the server clears the
  // previous lines when none are sent).
  form.set('serviceLines', JSON.stringify(lines.map((l) => ({
    entityId: l.entityId, service: l.service, serviceOther: l.service === 'Other' ? String(l.serviceOther || '').trim() : '',
    description: l.descriptionOfService || '', fees: split[lineFeeKey(l.k)] === undefined ? '' : String(split[lineFeeKey(l.k)]),
  }))));
  // Every answer a company gives on its own (master spelling).
  form.set('profileSplit', profileSplitJson(f));
  form.set('ssicPrimary', f.ssicPrimary);
  form.set('ssicSecondary', f.ssicSecondary);
  form.set('principalActivity1', f.principalActivity1);
  form.set('principalActivity2', f.principalActivity2);
  form.set('secRegistered', f.secRegistered === 'yes' ? 'true' : 'false');
  form.set('partnerStaffIds', JSON.stringify(f.partnerStaffIds));
  form.set('directorStaffIds', JSON.stringify(f.directorStaffIds));
  form.set('managerStaffIds', JSON.stringify(f.managerStaffIds));
  form.set('teamStaffIds', JSON.stringify(f.teamStaffIds));
  form.set('isGroupAudit', f.isGroupAudit === 'yes' ? 'true' : 'false');
  form.set('underInvestigation', f.underInvestigation === 'yes' ? 'true' : 'false');
  form.set('tcwgFeeCommunicated', f.tcwgFeeCommunicated === 'yes' ? 'true' : 'false');
  const screeningYes = screeningAsked && f.screeningRequired === 'yes';
  form.set('screeningRequired', screeningYes ? 'true' : 'false');
  if (screeningYes) appendScreeningAnswers(form, f);
  form.set('independenceDeclared', f.independenceDeclared ? 'true' : 'false');
  form.set('remarks', f.remarks);
  if (f.reviewerStaffId) form.set('reviewerStaffId', f.reviewerStaffId);
  (files || []).forEach((x) => form.append('files', x));
  return form;
};

// Return-comment keys for the shared engagement blocks — the same keys the
// detail page's CHANGE_LABELS and DetailItem anchors use. People fields diff
// (and are commented on) by NAME list there, not by staff-id list, so the two
// spellings meet here. Everything else keys by its own name.
const ENGREG_ANCHOR_KEYS = {
  partnerStaffIds: 'partnerNames',
  directorStaffIds: 'directorNames',
  managerStaffIds: 'managerNames',
  teamStaffIds: 'teamMembers',
};
export const engRegAnchor = (k) => ENGREG_ANCHOR_KEYS[k] || k;

// The shared form body: ① companies → ② engagement & client profile → ③ people.
// Attachments / declaration / remarks stay in the page (New vs edit differ).
// Comment anchors are wired unconditionally: on the New page there is no
// FieldCommentsProvider, so they render nothing; on the edit page a
// reviewer's comment shows under the control it talks about.
export const EngRegFormFields = ({ f, set, options, requestId, onDuplicates, requesterStaffId = null }) => {
  // The duplicate guard lives HERE, not in the two pages, so no surface that
  // renders this block can be shipped without it. The pages only get told, so
  // they can stop a submit that is already known to fail.
  // Companies answering the service / description on their own are each
  // asked with their own slot (slotQuestionOf).
  const slot = slotQuestionOf(f);
  const { duplicates, notices } = useDuplicateEngagements('/api/workflow/engreg/duplicate-check', slot.sharedIds, f.yearEnd, requestId,
    { service: f.service, serviceOther: f.serviceOther, description: f.descriptionOfService, rows: slot.rows });
  // The slots the form itself fills twice (a company's own service against
  // its lines) block the submit like a live-check clash.
  const companies = companiesOf(f, options);
  const formClashes = useMemo(
    () => serviceLineClashes(f, { companies, multiFyeServices: (options || {}).multiFyeServices || [] }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [f.entityIds, f.serviceLines, f.service, f.serviceOther, f.descriptionOfService, f.profileSplit, options]
  );
  const blocking = useMemo(() => [...duplicates, ...formClashes], [duplicates, formClashes]);
  useEffect(() => { if (onDuplicates) onDuplicates(blocking); }, [blocking, onDuplicates]);

  return (
    <>
      <FormSection step={1} title="Companies" hint={COMPANIES_SECTION_HINT}>
        <CompanyChecklist f={f} set={set} options={options} />
        <EngagementDateFields f={f} set={set} anchor={engRegAnchor} />
        {/* Directly under the two inputs that decide it — the answer to
            "companies × FYE × service", read where the question was asked
            (the service is picked in the next section; the answer updates). */}
        <DuplicateEngagementWarning duplicates={duplicates} />
      </FormSection>

      <FormSection step={2} title="Service & Client Profile">
        <EngagementProfileFields
          f={f} set={set} options={options || {}} anchor={engRegAnchor}
          feeCompanies={feeRowsOf(f, options)}
          companies={companies}
          descriptionNeeded={descriptionNeededFrom(duplicates, notices)}
          descriptionNotice={<DescriptionOfServiceNotice duplicates={duplicates} notices={notices} />}
          serviceLines duplicates={duplicates} notices={notices}
        />
      </FormSection>

      <FormSection step={3} title="People" hint={ENGREG_PEOPLE_HINT}>
        <EngagementPeopleFields f={f} set={set} options={options || {}} required={false} anchor={engRegAnchor}
          lockedTeamIds={requesterStaffId != null ? [requesterStaffId] : []} />
      </FormSection>
    </>
  );
};

// The Reviewer hint of this flow: the reviewer's department decides whether
// the Screening section is on the form (New page and edit page alike).
export const ENGREG_REVIEWER_HINT = "You pick who reviews this request, from the firm's Partners and the QM department. QM screening can be requested only with a reviewer from the QM department; with any other reviewer you may still attach your own screening document.";

// The Screening section says what it is for in one line — the same wording on
// the New page, the Detail page and the edit page.
export const SCREENING_SECTION_HINT = (
  <>The screening (background check) for this registration. <InfoTip text="Yes — answer the screening questions; approval opens a Screening request in which QM performs the check and files the document. No — you may attach your own screening document here." /></>
);
// The reviewer is outside QM: no question, only the requester's own document.
export const SCREENING_DOC_ONLY_HINT = 'The reviewer is not from the QM department, so QM does not perform the screening. You may attach your own screening document.';

/**
 * The screening block — the requester's side of it.
 *
 * Yes ⇒ the six screening questions (mandatory); QM does the screening in the
 * Screening request the approval opens, so there is nothing to upload and an
 * input would invite a file the server refuses. No ⇒ an optional upload. Before the question is answered the
 * block says so instead of guessing — the answer lives a section above (it is
 * an engagement field, shared with Management and Amendment), so a silent empty
 * box would read as "nothing required here".
 *
 * docOnly (the reviewer is outside QM) ⇒ no question, only the optional upload.
 */
export const ScreeningUpload = ({
  f, set, screeningFile, setScreeningFile, existing = [], currentBlock = null,
  anchor = engRegAnchor, required = true, blankNote = null, docOnly = false, companies = [],
}) => {
  const current = screeningFileOf(existing);
  const upload = (
    <Field
      label="Screening Document"
      hint={current
        ? `Currently attached: ${current.originalName}. Choosing a file replaces it.`
        : hintOf('pdfOnly')}
    >
      <FilePicker kind="pdfOnly"
        onChange={(e) => setScreeningFile((e.target.files || [])[0] || null)}
        files={screeningFile} />
    </Field>
  );
  if (docOnly) return <>{currentBlock}{upload}</>;
  return (
    <>
      {/* Question first, then what is already on file, then the picker — the
          same order the Attachments block uses. The middle
          slot is passed in because only an edit surface has a removable list;
          the New page has nothing on file yet. */}
      <EngagementScreeningQuestion f={f} set={set} anchor={anchor} required={required} />
      {/* Optional (Engagement Amendment): blank = no change, and a radio
          cannot be un-picked on its own. */}
      {!required && f.screeningRequired !== '' && (
        <button type="button" onClick={() => { set('screeningRequired', ''); setScreeningFile(null); }}
          className="text-xs text-blue-600 hover:text-blue-800 -mt-2">
          Clear Answer
        </button>
      )}
      {currentBlock}
      {f.screeningRequired === '' && (required ? (
        <p className="text-sm text-amber-700">Answer the question above to see what to attach.</p>
      ) : blankNote && (
        <p className="text-sm text-slate-500">{blankNote}</p>
      ))}
      {f.screeningRequired === 'yes' && (
        <>
          <p className="text-sm text-slate-600">
            QM performs the screening after approval.
          </p>
          <ScreeningQuestionFields f={f} set={set} anchor={anchor} companies={companies} />
        </>
      )}
      {f.screeningRequired === 'no' && upload}
    </>
  );
};

// The declaration is not a Field, so it marks itself the same way one would
// (red text, halo, caption) when Submit found it unticked.
export const IndependenceLine = ({ checked, onChange }) => {
  const invalid = useFieldInvalid('independenceDeclared');
  return (
    <div className={invalid ? fieldInvalidRing : undefined} {...(invalid ? { [INVALID_FIELD_ATTR]: 'independenceDeclared' } : {})}>
      <label className={`flex items-start gap-2 text-sm ${invalid ? fieldInvalidLabel : 'text-slate-700'}`}>
        <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-0.5" />
        <span>I confirm that every person named on this project is independent of the client and unaware of any threats to independence or conflict-of-interest situations (EP 100, IESBA Code, ACRA Code, and the firm's Quality Management Manual).</span>
      </label>
      <FieldInvalidInline field="independenceDeclared" text="Required — please confirm the declaration" />
    </div>
  );
};

const WorkflowEngRegNew = () => {
  const navigate = useNavigate();
  const [options, setOptions] = useState(null);
  const [f, setF] = useState(emptyEngRegForm());
  const [files, setFiles] = useState([]);
  const [bizFiles, setBizFiles] = useBizFiles(f.entityIds);
  const [screeningFile, setScreeningFile] = useState(null);
  const [duplicates, setDuplicates] = useState([]);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));

  useEffect(() => {
    (async () => {
      try {
        const data = await fetchJSON('/api/workflow/engreg/options');
        setOptions(data);
        // The requester starts on the team, locked there.
        if (data.requesterStaffId != null) {
          setF((prev) => (prev.teamStaffIds.includes(data.requesterStaffId)
            ? prev : { ...prev, teamStaffIds: [data.requesterStaffId, ...prev.teamStaffIds] }));
        }
      } catch (e) { setError(e.message); }
    })();
  }, []);

  // Everything still empty, keyed to the control it belongs to — after the
  // first Submit the page marks those controls and lists them in the banner.
  // The declaration is this page's own (the edit page has no checkbox).
  // The screening question is asked only with a reviewer from the QM department.
  const screeningAsked = useIsQmReviewer(f.reviewerStaffId);
  const gaps = useMemo(() => [
    ...engRegRequiredGaps(f, {
      bizFiles,
      entities: (options || {}).entities || [],
      requesterStaffId: options ? (options.requesterStaffId ?? null) : undefined,
      multiFyeServices: (options || {}).multiFyeServices || [],
      screeningAsked,
      options,
    }),
    ...(f.independenceDeclared ? [] : [{ key: 'independenceDeclared', label: 'Independence Declaration' }]),
  ], [f, bizFiles, options, screeningAsked]);
  const validation = useFormValidation(gaps);

  const submit = async () => {
    // Marks the empty fields and scrolls to the first; the banner comes from
    // `validation.error` and clears itself as the gaps are filled.
    if (!validation.validate()) { setError(null); return; }
    // Already warned about above the fold — repeat it here rather than spend a
    // round trip earning the identical sentence from the server.
    if (duplicates.length > 0) { setError(duplicates[0].message); return; }
    setSaving(true); setError(null);
    try {
      const data = await fetchJSON('/api/workflow/engreg', {
        method: 'POST',
        // A screening file picked before flipping the answer back to Yes is
        // dropped here rather than sent for the server to reject.
        body: engRegFormData(f, files, bizFiles, !screeningAsked || f.screeningRequired === 'no' ? screeningFile : null, screeningAsked),
      });
      navigate(`/workflow/engreg/${data.request.id}`);
    } catch (e) { setError(e.message); setSaving(false); }
  };

  return (
    <FormShell
      backTo="/workflow/engreg"
      backLabel="Engagement Registration"
      title="New Engagement Registration Request"
      subtitle="Register the engagements of a new project."
      error={error || validation.error}
      onSubmit={submit}
      submitting={saving}
    >
      <FormValidationProvider value={validation.providerValue}>
        <EngRegFormFields f={f} set={set} options={options} onDuplicates={setDuplicates}
          requesterStaffId={(options || {}).requesterStaffId ?? null} />

        {/* BizFile is its own step, NOT part of Attachments:
            one is a mandatory named document per company, the other is free-form
            supporting material. Putting them in one box makes the required upload
            look optional. */}
        <FormSection step={4} title="BizFile">
          <BizFileUploads f={f} options={options} bizFiles={bizFiles} setBizFiles={setBizFiles} />
        </FormSection>

        <FormSection step={5} title="Attachments">
          <Field label="Supporting Files" hint={hintOf('docsSheetsImages', { multiple: true })}>
            <FilePicker multiple kind="docsSheetsImages"
              onChange={(e) => setFiles(Array.from(e.target.files || []))}
              files={files} />
          </Field>
        </FormSection>

        <FormSection step={6} title="Declaration & Remarks">
          <IndependenceLine checked={f.independenceDeclared} onChange={(v) => set('independenceDeclared', v)} />
          <Field label="Remarks">
            <textarea rows="2" value={f.remarks} onChange={(e) => set('remarks', e.target.value)} className={inputCls} />
          </Field>
        </FormSection>

        <FormSection step={7} title="Reviewer">
          <Field label="Reviewer" required field="reviewerName" hint={ENGREG_REVIEWER_HINT}>
            <ReviewerSelect roster="master" value={f.reviewerStaffId} onChange={(v) => set('reviewerStaffId', v)} />
          </Field>
        </FormSection>

        {/* Screening comes after the Reviewer: with a reviewer from the QM
            department its own question decides who files the screening
            document; with anyone else only the requester's upload shows. */}
        {f.reviewerStaffId && (
          <FormSection step={8} title="Screening" hint={screeningAsked ? SCREENING_SECTION_HINT : SCREENING_DOC_ONLY_HINT}>
            <ScreeningUpload f={f} set={set} screeningFile={screeningFile} setScreeningFile={setScreeningFile} docOnly={!screeningAsked} companies={companiesOf(f, options)} />
          </FormSection>
        )}
      </FormValidationProvider>
    </FormShell>
  );
};

export default WorkflowEngRegNew;
